import {
	Alert,
	DocumentTree,
	type DocumentTreeItem,
	type DocumentTreeItemStatus,
} from "@exopengithub/exokit";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "molecules/input/Input";
import { getReportOpenMode } from "src/features/excelReports/utils/reportOpenMode";
import { DashboardWidgetBase } from "./DashboardWidgetBase.js";

export const EXCEL_SETUP_INSTRUCTIONS_URL =
	"https://exopen.com/docs/installera-och-anvanda-exopen-for-excel.pdf";

export type ExcelReportsWidgetStatus =
	| "loading"
	| "error"
	| "no-access"
	| "ready";

interface ExcelReportsWidgetViewProps {
	status: ExcelReportsWidgetStatus;
	items: DocumentTreeItem[];
	rootId?: string | undefined;
	onOpenFile: (item: DocumentTreeItem) => void;
	getItemStatus?: (item: DocumentTreeItem) => DocumentTreeItemStatus;
	/** After a launch: the page can't tell whether Excel actually opened. */
	showLaunchNote?: boolean;
	onDismissLaunchNote?: () => void;
	/** Prefills the search, e.g. from a start-page search hit. */
	initialQuery?: string | undefined;
	/** Pinned right in the widget header, e.g. a link to another reports home. */
	action?: ReactNode;
	/** Lets the tree grow with its content instead of scrolling in a capped box — for a page with room below. */
	growWithContent?: boolean;
}

export const ExcelReportsWidgetView = ({
	status,
	items,
	rootId,
	onOpenFile,
	getItemStatus,
	showLaunchNote = false,
	onDismissLaunchNote,
	initialQuery = "",
	action,
	growWithContent = false,
}: ExcelReportsWidgetViewProps) => {
	const { t } = useTranslation();
	const [query, setQuery] = useState(initialQuery);
	// PDF and Word reports open in a browser tab, so the Excel hint only applies when there is an Excel report.
	const hasExcelReports = items.some(
		(item) => item.type === "file" && getReportOpenMode(item.name) === "excel",
	);

	if (status === "error" || status === "no-access") {
		return (
			<DashboardWidgetBase title={t("Reports")} action={action}>
				<p className="text-sm text-grey-600">
					{status === "error"
						? t("The reports could not be loaded")
						: t("You don't have access to any reports")}
				</p>
			</DashboardWidgetBase>
		);
	}

	return (
		<DashboardWidgetBase title={t("Reports")} action={action} flush>
			<div className="flex flex-col gap-3 p-4">
				<Input
					type="search"
					value={query}
					onChange={(event) => setQuery(event.target.value)}
					placeholder={t("Search reports")}
					ariaLabel={t("Search reports")}
				/>
				<DocumentTree
					// WHY capped: the tree is virtualized and scrolls inside the card instead of stretching its row.
					className={growWithContent ? undefined : "max-h-72"}
					items={items}
					rootId={rootId}
					query={query}
					onOpenFile={onOpenFile}
					getItemStatus={getItemStatus}
					renderFileTooltip={(item) => getItemStatus?.(item).disabledReason}
					isLoading={status === "loading"}
					emptyState={
						<p className="p-3 text-sm text-grey-600">
							{t("There are no reports yet")}
						</p>
					}
					labels={{
						loading: t("Loading"),
						noMatches: t("No reports match your search"),
					}}
					aria-label={t("Reports")}
				/>
				{showLaunchNote && (
					<Alert
						variant="info"
						title={t("Didn't Excel open?")}
						withClose
						dismissAriaLabel={t("Close")}
						onCloseClick={onDismissLaunchNote}
						supportingText={
							<>
								{t("Make sure Excel for desktop is installed.")}{" "}
								<a
									href={EXCEL_SETUP_INSTRUCTIONS_URL}
									target="_blank"
									rel="noopener noreferrer"
									className="underline"
								>
									{t("See the setup instructions")}
								</a>
							</>
						}
					/>
				)}
				{hasExcelReports && (
					<p className="text-xs text-grey-500">
						{t(
							"Excel reports open in Excel. Click “Enable Editing” if Excel shows Protected View.",
						)}
					</p>
				)}
			</div>
		</DashboardWidgetBase>
	);
};
